import { useEffect, useState, useCallback } from 'react'
import { Zap, Plus, X, Trash2, RefreshCw, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Exp {
  id: number
  name: string
  hypothesis: string
  target_service: string
  run_at: string
  run_by: string
  status: string
  blast_radius: string
  learnings: string
  runbook_url: string | null
}

const STATUS = ['planned', 'running', 'passed', 'failed', 'aborted'] as const
const BLAST   = ['canary', 'one-zone', 'one-region', 'global']

const STATUS_TONE: Record<string, string> = {
  planned: 'bg-blue/15 text-blue-light border-blue/30',
  running: 'bg-amber/15 text-amber-light border-amber/30',
  passed:  'bg-emerald/15 text-emerald border-emerald/30',
  failed:  'bg-rose/15 text-rose border-rose/30',
  aborted: 'bg-surface-raised text-text-muted border-border',
}

export default function ChaosPage() {
  const [items, setItems] = useState<Exp[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState<string>('all')
  const [form, setForm] = useState({
    name: '', hypothesis: '', target_service: '',
    run_at: new Date().toISOString().slice(0, 16),
    run_by: '', status: 'planned', blast_radius: 'canary',
    learnings: '', runbook_url: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sre/chaos')
      setItems(r.data?.experiments || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.name) return
    await api.post('/api/sre/chaos', { ...form, run_at: new Date(form.run_at).toISOString() })
    setShowForm(false)
    setForm({ ...form, name: '', hypothesis: '', learnings: '' })
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete experiment?')) return
    await api.delete(`/api/sre/chaos/${id}`)
    fetchAll()
  }

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter)

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Zap size={24} className="text-amber-light" /> Chaos Experiments
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Log ChaosMesh / Litmus / game-day runs. Hypothesis · blast radius · pass-fail · learnings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Log experiment</button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setFilter('all')}
          className={clsx('badge text-[10px] px-2 py-1', filter === 'all' ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
          All ({items.length})
        </button>
        {STATUS.map(s => {
          const n = items.filter(i => i.status === s).length
          return (
            <button key={s} type="button" onClick={() => setFilter(s === filter ? 'all' : s)}
              className={clsx('badge text-[10px] px-2 py-1',
                filter === s ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
              {s} <span className="text-text-muted ml-1">{n}</span>
            </button>
          )
        })}
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Log chaos experiment</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder='Name e.g. "Kill primary RDS"' className="input py-1.5 md:col-span-2" />
            <textarea value={form.hypothesis} onChange={e => setForm({...form, hypothesis: e.target.value})} rows={2}
              placeholder='Hypothesis e.g. "Failover completes <60s without user impact"' className="input py-1.5 md:col-span-2" />
            <input value={form.target_service} onChange={e => setForm({...form, target_service: e.target.value})} placeholder="Target service" className="input py-1.5" />
            <input value={form.run_by} onChange={e => setForm({...form, run_by: e.target.value})} placeholder="Run by" className="input py-1.5" />
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input py-1.5">
              {STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={form.blast_radius} onChange={e => setForm({...form, blast_radius: e.target.value})} className="input py-1.5">
              {BLAST.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <label className="space-y-0.5"><span className="text-text-muted">Run at</span>
              <input type="datetime-local" value={form.run_at} onChange={e => setForm({...form, run_at: e.target.value})} className="input w-full py-1.5" /></label>
            <input value={form.runbook_url} onChange={e => setForm({...form, runbook_url: e.target.value})} placeholder="Runbook URL" className="input py-1.5" />
            <textarea value={form.learnings} onChange={e => setForm({...form, learnings: e.target.value})} rows={3}
              placeholder='Learnings — what we found, what we changed' className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">No experiments logged.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Service</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-left py-2 px-2">Blast</th>
                <th className="text-left py-2 px-2">Run at</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2">
                    <div className="text-text-primary font-medium">{e.name}</div>
                    {e.hypothesis && <div className="text-[10px] text-text-muted line-clamp-1">{e.hypothesis}</div>}
                  </td>
                  <td className="py-1.5 px-2 text-text-secondary">{e.target_service || '—'}</td>
                  <td className="py-1.5 px-2">
                    <span className={clsx('badge text-[9px] uppercase', STATUS_TONE[e.status])}>{e.status}</span>
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">{e.blast_radius}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{e.run_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1.5 px-2 text-right space-x-2">
                    {e.runbook_url && <a href={e.runbook_url} target="_blank" rel="noreferrer" className="text-blue-light"><ExternalLink size={10} /></a>}
                    <button type="button" onClick={() => remove(e.id)} className="text-text-muted hover:text-rose"><Trash2 size={10} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
